import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from 'app/services/apiService';

// Fetch user settings
export const fetchUserSettings = createAsyncThunk(
    'userSettings/fetchUserSettings',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getSettings();
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.message);
        }
    }
);

// Update user settings
export const updateUserSettings = createAsyncThunk(
    'userSettings/updateUserSettings',
    async ({ key, value }, { rejectWithValue, getState }) => {
        try {
            const { userSettings } = getState();

            // Merge if the key points to an object or array, otherwise replace it
            const existingValue = userSettings?.settings?.[key];
            const isSameType =
                (Array.isArray(existingValue) && Array.isArray(value)) ||
                (typeof existingValue === 'object' && existingValue !== null &&
                 typeof value === 'object' && value !== null);
            
            const updatedPayload = {
                ...userSettings?.settings,
                [key]: isSameType
                    ? Array.isArray(existingValue)
                        ? [...existingValue, ...value]  // Merge arrays
                        : { ...existingValue, ...value } // Merge objects
                    : value // Replace if types don't match or if either is a primitive
            };

            await apiService.updateUserSettings(updatedPayload);

            return { key, value };
        } catch (error) {
            return rejectWithValue(error?.message);
        }
    }
);

export const userSettingsSlice = createSlice({
    name: 'userSettings',
    initialState: {
        settings: {},
        updated: false,
    },
    reducers: {
        setSettings(state, { payload }) {
            state.settings = { ...state.settings, ...payload };
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchUserSettings.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchUserSettings.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.settings = payload?.data?.settings || {};
            })
            .addCase(fetchUserSettings.rejected, (state) => {
                state.loading = false;
            })
            .addCase(updateUserSettings.fulfilled, (state, action) => {
                state.updated = true;
                const { key, value } = action.payload;
                const isObject = state.settings[key] && typeof state.settings[key] === 'object';
                state.settings[key] = isObject ? { ...state.settings[key], ...value } : value;
            })
            .addCase(updateUserSettings.rejected, (state) => {
                state.updated = false;
            });
    },
});

export const { setSettings } = userSettingsSlice.actions;
export default userSettingsSlice.reducer;